/**
 * TrainingSidebar Component
 * Displays training compliance and quick actions
 */

import React from 'react';
import { Award, Plus, History, Download, Zap } from 'lucide-react';

const TrainingSidebar = React.memo(({
  trainingRecords,
  stats,
  onQuickAction
}) => {
  // Calculate compliance based on certificates issued vs total sessions
  const totalSessions = stats?.total || trainingRecords.length;
  const certificatesIssued = trainingRecords.filter(r => r.certificateIssued).length;
  const compliancePercentage = totalSessions > 0
    ? Math.round((certificatesIssued / totalSessions) * 100)
    : 0;

  // Status counts
  const thisMonth = stats?.thisMonth || 0;
  const pending = totalSessions - certificatesIssued;

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-950/5 p-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Award className="h-5 w-5 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">Training Summary</h3>
        </div>

        {/* Compliance Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-gray-600">Certificate Completion</span>
            <span className="text-sm font-semibold text-gray-900">{compliancePercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                compliancePercentage >= 90 ? 'bg-green-600' :
                compliancePercentage >= 70 ? 'bg-amber-500' : 'bg-red-600'
              }`}
              style={{ width: `${compliancePercentage}%` }}
            />
          </div>
        </div>

        {/* Status Counts - COLORED DOTS (not custom boxes!) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Certified</span>
            </div>
            <span className="text-sm font-semibold text-green-600">{certificatesIssued}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-sm text-gray-600">This Month</span>
            </div>
            <span className="text-sm font-semibold text-amber-600">{thisMonth}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Pending</span>
            </div>
            <span className="text-sm font-semibold text-gray-600">{pending}</span>
          </div>
        </div>

        {/* Total */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Total</span>
            <span className="text-sm font-semibold text-gray-900">{totalSessions} sessions</span>
          </div>
        </div>
      </div>

      {/* Quick Actions Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Zap className="h-5 w-5 text-white" />
            Quick Actions
          </h3>
        </div>
        <div className="p-6 space-y-2">
          {/* FT-16: min-h-[44px] ensures 44px+ touch targets on mobile */}
          <button
            onClick={() => onQuickAction('add')}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 text-base md:text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 min-h-[44px]"
          >
            <Plus className="h-5 w-5 md:h-4 md:w-4" />
            Add Training
          </button>
          <button
            onClick={() => onQuickAction('export')}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
          >
            <Download className="h-5 w-5 md:h-4 md:w-4" />
            Export
          </button>
          <button
            onClick={() => onQuickAction('history')}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2 text-base md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px]"
          >
            <History className="h-5 w-5 md:h-4 md:w-4" />
            History
          </button>
        </div>
      </div>
    </div>
  );
});

TrainingSidebar.displayName = 'TrainingSidebar';

export default TrainingSidebar;
